import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import ConfirmationModal from '@components/common/modals/ConfirmationModal';
import ImportSettings from '@components/features/import/ImportSettings';
import ScopeRulesSettings from '@components/features/import/ScopeRulesSettings';
import SystemParametersSettings from '@components/features/system-parameters/SystemParametersSettings';
import JobSchedulesSettings from '@components/features/job-schedules/JobSchedulesSettings';
import ReferenceListEditor from '@components/features/reference-lists/ReferenceListEditor';
import TierLabelsSettings from '@components/features/reference-lists/TierLabelsSettings';
import { AuditLogExport } from '@components/features/audit';
import { WidgetAccessSettings } from '@components/features/dashboard-widgets';
import { referenceListService } from '@services';
import type { ReferenceList } from '@app-types/reference-list';
import { showToast } from '@utils/toast';
import SettingsSidebar from './SettingsSidebar';
import {
  AUDIT_LOG_SECTION,
  DEFAULT_SECTION,
  REFERENCE_LISTS_LEGACY_SECTION,
  SUPPLIER_TIERS_SECTION,
  WIDGET_ACCESS_SECTION,
  buildSettingsNavGroups,
  fromRefSection,
  resolveDefaultRefSection,
  resolveLegacySection,
  toRefSection,
} from './settingsNav';
import styles from './SettingsLayout.module.css';

const SettingsLayout: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSection = resolveLegacySection(searchParams.get('tab'), searchParams.get('section'));

  const [lists, setLists] = useState<ReferenceList[]>([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [search, setSearch] = useState('');
  const [pendingSection, setPendingSection] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadLists = async () => {
      try {
        setListsLoading(true);
        const data = await referenceListService.getAll();
        if (!cancelled) {
          setLists(data);
        }
      } catch {
        if (!cancelled) {
          showToast.error('Failed to load reference lists');
          setLists([]);
        }
      } finally {
        if (!cancelled) {
          setListsLoading(false);
        }
      }
    };

    void loadLists();
    return () => {
      cancelled = true;
    };
  }, []);

  const navGroups = useMemo(() => buildSettingsNavGroups(lists), [lists]);

  const setSection = useCallback(
    (section: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', 'settings');
      next.set('section', section);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (requestedSection !== REFERENCE_LISTS_LEGACY_SECTION) {
      return;
    }
    if (listsLoading) {
      return;
    }
    setSection(resolveDefaultRefSection(lists));
  }, [lists, listsLoading, requestedSection, setSection]);

  const handleSelect = useCallback(
    (section: string) => {
      if (section === requestedSection) {
        return;
      }
      if (hasChanges) {
        setPendingSection(section);
        return;
      }
      setSection(section);
    },
    [hasChanges, requestedSection, setSection]
  );

  const handleConfirmSwitch = useCallback(() => {
    if (pendingSection) {
      setHasChanges(false);
      setSection(pendingSection);
    }
    setPendingSection(null);
  }, [pendingSection, setSection]);

  const handleListSaved = useCallback((updated: ReferenceList) => {
    setLists((prev) => prev.map((list) => (list.key === updated.key ? updated : list)));
  }, []);

  const refKey = fromRefSection(requestedSection);
  const selectedList = refKey ? lists.find((list) => list.key === refKey) : undefined;

  const renderPane = () => {
    switch (requestedSection) {
      case DEFAULT_SECTION:
        return <ImportSettings key={requestedSection} onDirtyChange={setHasChanges} />;
      case 'scope-rules':
        return <ScopeRulesSettings key={requestedSection} onDirtyChange={setHasChanges} />;
      case 'timing-thresholds':
        return <SystemParametersSettings key={requestedSection} onDirtyChange={setHasChanges} />;
      case 'job-schedules':
        return <JobSchedulesSettings key={requestedSection} onDirtyChange={setHasChanges} />;
      case SUPPLIER_TIERS_SECTION:
        return <TierLabelsSettings key={requestedSection} onDirtyChange={setHasChanges} />;
      case AUDIT_LOG_SECTION:
        return <AuditLogExport />;
      case WIDGET_ACCESS_SECTION:
        return <WidgetAccessSettings />;
      case REFERENCE_LISTS_LEGACY_SECTION:
        return (
          <div className={styles.loading} data-test-id="settings-reference-lists-redirect">
            <Loader2 size={32} className="spin" />
            <p>Loading reference lists...</p>
          </div>
        );
      default:
        if (!refKey) {
          return <div className={styles.empty}>Select a settings category.</div>;
        }
        if (listsLoading) {
          return (
            <div className={styles.loading} data-test-id="reference-lists-loading">
              <Loader2 size={32} className="spin" />
              <p>Loading reference list...</p>
            </div>
          );
        }
        if (!selectedList) {
          return (
            <div className={styles.empty} data-test-id="reference-lists-empty">
              This reference list is not configured.
            </div>
          );
        }
        return (
          <ReferenceListEditor
            key={toRefSection(selectedList.key)}
            list={selectedList}
            onDirtyChange={setHasChanges}
            onSaved={handleListSaved}
          />
        );
    }
  };

  return (
    <div className={styles.layout} data-test-id="settings-layout">
      <SettingsSidebar
        groups={navGroups}
        selectedId={requestedSection}
        hasChanges={hasChanges}
        search={search}
        onSearchChange={setSearch}
        onSelect={handleSelect}
      />
      <div className={styles.pane} data-test-id="settings-pane">
        {renderPane()}
      </div>

      <ConfirmationModal
        isOpen={pendingSection !== null}
        onClose={() => setPendingSection(null)}
        onConfirm={handleConfirmSwitch}
        title="Discard unsaved changes?"
        message="You have unsaved changes. Switching settings will discard them. This action cannot be undone."
        confirmLabel="Discard & Switch"
        confirmVariant="danger"
        dataTestId="switch-settings-confirmation-modal"
      />
    </div>
  );
};

export default SettingsLayout;
